"use client";

import { useState, FormEvent } from "react";
import { signIn } from "next-auth/react";
import { useTranslations } from "@i18n/provider";
import { PageShell } from "@ui/PageShell";
import { ConvoIcon } from "@ui/ConvoIcon";

export default function LoginForm({
  devBypassEnabled,
  googleEnabled,
}: {
  devBypassEnabled: boolean;
  googleEnabled: boolean;
}) {
  const { t } = useTranslations();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [isDevSigningIn, setIsDevSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await signIn("nodemailer", {
        email: email.trim(),
        redirect: false,
        callbackUrl: "/",
      });

      if (result?.error) {
        setError(t("login.sendError"));
        return;
      }

      // A full document load, not router.push: signIn() has just set the session
      // cookie, and a client-side navigation can still serve the RSC payload that
      // was rendered for the signed-out user.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = "/login/verify";
    } catch {
      setError(t("login.sendError"));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    if (isGoogleSigningIn) return;
    setError(null);
    setIsGoogleSigningIn(true);

    try {
      await signIn("google", { callbackUrl: "/" });
    } catch {
      setError(t("login.sendError"));
      setIsGoogleSigningIn(false);
    }
  }

  async function handleDevBypass() {
    if (isDevSigningIn) return;
    setError(null);
    setIsDevSigningIn(true);

    try {
      const result = await signIn("dev-bypass", {
        redirect: false,
        callbackUrl: "/",
      });

      if (result?.error) {
        setError(t("login.devBypassError"));
        return;
      }

      // Full document load for the same reason as the magic-link branch above.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = "/";
    } catch {
      setError(t("login.devBypassError"));
    } finally {
      setIsDevSigningIn(false);
    }
  }

  return (
    <PageShell
      narrow
      className="flex min-h-[70vh] flex-col items-center justify-center gap-6 py-16"
    >
      <div className="surface-card w-full max-w-sm p-8">
        <div className="mb-6 text-center">
          <ConvoIcon
            className="mx-auto h-12 w-12"
            style={{ filter: "drop-shadow(0 4px 12px rgba(201, 106, 69, 0.5))" }}
          />
          <h1 className="font-display mt-4 text-2xl font-bold lowercase text-ink">convo</h1>
          <p className="mt-2 text-sm text-ink-muted">{t("login.subtitle")}</p>
        </div>

        <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
          <label className="sr-only" htmlFor="login-email">
            {t("login.emailLabel")}
          </label>
          <input
            id="login-email"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("login.emailPlaceholder")}
            required
            autoComplete="email"
            className="inset-field"
          />
          {error ? (
            <p role="alert" className="text-sm text-writing-fg">
              {error}
            </p>
          ) : null}
          <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
            {isSubmitting ? t("login.sending") : t("login.sendLink")}
          </button>
        </form>

        {googleEnabled ? (
          <div className="mt-4">
            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-surface-border" />
              <span className="mx-3 flex-shrink text-xs text-ink-faint">
                {t("login.orDivider")}
              </span>
              <div className="flex-grow border-t border-surface-border" />
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isGoogleSigningIn}
              className="flex w-full items-center justify-center gap-3 rounded-full border border-surface-border bg-surface px-5 py-2.5 text-sm font-semibold text-ink-body transition-colors hover:bg-coral-tint-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
                  fill="#4285F4"
                />
                <path
                  d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
                  fill="#34A853"
                />
                <path
                  d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
                  fill="#FBBC05"
                />
                <path
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z"
                  fill="#EA4335"
                />
              </svg>
              {isGoogleSigningIn ? t("login.signInGoogleLoading") : t("login.signInGoogle")}
            </button>
          </div>
        ) : null}

        {devBypassEnabled ? (
          <div className="mt-6 border-t border-surface-border pt-6">
            <p className="mb-3 text-center text-xs text-ink-faint">{t("login.devBypassHint")}</p>
            <button
              type="button"
              data-testid="login-dev-bypass"
              onClick={handleDevBypass}
              disabled={isDevSigningIn}
              className="w-full rounded-full border border-surface-border bg-surface px-5 py-2.5 text-sm font-semibold text-ink-body transition-colors hover:bg-coral-tint-soft disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isDevSigningIn ? t("login.devBypassLoading") : t("login.devBypass")}
            </button>
          </div>
        ) : null}
      </div>

      <a
        href="https://convergic.io"
        className="text-xs text-ink-faint transition-colors hover:text-coral-fg"
      >
        {t("login.madeBy")}
      </a>
    </PageShell>
  );
}
